/**
 * Map an inline gTwin scene item to a `.glayer` layer.json payload (WP-E).
 */

import { ARTIFACT_SCENE_CONTRACT } from '@/sdk-stub/gtwin-sdk';
import type { StudioSceneItemEntry } from './sceneStructure';

function normalizeEndpoints(raw: unknown): Record<string, { href: string }> {
  const out: Record<string, { href: string }> = {};
  if (!raw || typeof raw !== 'object') return out;
  for (const [key, val] of Object.entries(raw as Record<string, unknown>)) {
    if (typeof val === 'string' && val.trim()) {
      out[key] = { href: val };
    } else if (val && typeof val === 'object' && 'href' in val) {
      const href = (val as { href?: unknown }).href;
      if (typeof href === 'string' && href.trim()) out[key] = { href };
    }
  }
  return out;
}

function inferArtifactKind(item: Record<string, unknown>): string {
  const style = item.style as Record<string, unknown> | undefined;
  if (typeof style?.artifactKind === 'string') return style.artifactKind;
  if (typeof item.artifactKind === 'string') return item.artifactKind;
  const endpoints = normalizeEndpoints(item.endpoints);
  if (endpoints.cog) return 'cog';
  if (endpoints.pmtiles) return 'pmtiles';
  if (endpoints.tileset3d) return '3dtiles';
  if (endpoints.features || endpoints.geojson) return 'geojson';
  return 'unknown';
}

/** Build the SceneItem dict written into a thin `.glayer` archive. */
export function sceneItemToGlayerLayer(entry: StudioSceneItemEntry): Record<string, unknown> {
  const { slot, item } = entry;
  const artifactKind = inferArtifactKind(item);
  const contract = ARTIFACT_SCENE_CONTRACT[artifactKind as keyof typeof ARTIFACT_SCENE_CONTRACT];
  const layerKind =
    (typeof item.layerKind === 'string' && item.layerKind) ||
    contract?.layerKind ||
    'unknown';

  const layer: Record<string, unknown> = {
    id: String(item.id),
    layerKind,
    artifactKind,
    slot,
    endpoints: normalizeEndpoints(item.endpoints),
  };

  if (typeof item.title === 'string') layer.title = item.title;
  if (item.visible === false) layer.visible = false;
  if (typeof item.opacity === 'number') layer.opacity = item.opacity;
  if (typeof item.crs === 'string') layer.crs = item.crs;
  if (typeof item.sourceCrs === 'string') layer.sourceCrs = item.sourceCrs;
  if (Array.isArray(item.bbox)) layer.bbox = item.bbox;
  if (item.style && typeof item.style === 'object') layer.style = item.style;

  return layer;
}
